import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { MapPin, Calendar, Clock, Shirt, Coffee } from "lucide-react";
import { loadPrivateJob } from "../../../../services/jobService";
import { applyToJob, cancelApplication } from "../../../../services/applicationService";
import EmptyState from "../../../../Components/EmptyState/EmptyState";
import Toast from "../../../../Components/Toast/Toast"
import "./PrivateJobPage.scss";

function getErrorMessage(error, fallbackMessage) {
  const data = error?.response?.data;

  if (typeof data === "string" && data.trim()) {
    return data;
  }

  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message;
  }

  if (typeof data?.error === "string" && data.error.trim()) {
    return data.error;
  }

  // Erros de validação do ASP.NET.
  if (data?.errors && typeof data.errors === "object") {
    const firstError = Object.values(data.errors)
      .flat()
      .find((message) => typeof message === "string" && message.trim());

    if (firstError) {
      return firstError;
    }
  }

  return fallbackMessage;
}

// ─── Modal de perguntas ─────────────────────────────────────────────
function QuestionsModal({ questions = [], onConfirm, onClose, showToast }) {
  const [answers, setAnswers] = useState(() =>
    Object.fromEntries((questions || []).map(q => [q.id, null]))
  );

  const allAnswered = useMemo(
    () => questions.every(q => answers[q.id] !== null),
    [questions, answers]
  );

  const handleConfirm = () => {
    const failed = questions.filter(q => answers[q.id] !== q.expectedAnswer);

    if (failed.length > 0) {
      showToast(
        "Você não atende aos requisitos desta vaga.",
        "error"
      );
      return;
    }

    onConfirm();
  };

  return (
    <div className="pvq-overlay" onClick={onClose}>
      <div className="pvq-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Filtro da vaga</h3>
        <p>Responda para continuar</p>

        <div className="pvq-list">
          {questions.map((q, idx) => (
            <div key={q.id ?? idx} className="pvq-question">
              <p>{idx + 1}. {q.question}</p>

              <div className="pvq-buttons">
                <button
                  className={answers[q.id] === "sim" ? "active" : ""}
                  onClick={() => setAnswers(a => ({ ...a, [q.id]: "sim" }))}
                >
                  Sim
                </button>

                <button
                  className={answers[q.id] === "nao" ? "active" : ""}
                  onClick={() => setAnswers(a => ({ ...a, [q.id]: "nao" }))}
                >
                  Não
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="pvq-actions">
          <button onClick={onClose}>Cancelar</button>
          <button disabled={!allAnswered} onClick={handleConfirm}>
            Confirmar candidatura
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────
export default function PrivateJobPage() {
  const { slug } = useParams();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [status, setStatus] = useState(null);
  const [applying, setApplying] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [toast, setToast] = useState({
    message: "",
    type: "success",
  });

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  useEffect(() => {
    const fetchJob = async () => {
      try {
        setLoading(true);

        const data = await loadPrivateJob(slug);
        setJob(data);
        setStatus(data?.applicationStatus ?? null);
      } catch (err) {
        console.error(err);
        setError("Vaga não encontrada ou link inválido.");
      } finally {
        setLoading(false);
      }
    };

    if (slug) fetchJob();
  }, [slug]);

  const questions = job?.questions ?? [];
  const hasQuestions = questions.length > 0;

  const doApply = async () => {
    if (!job?.id) return;

    setApplying(true);
    try {
      await applyToJob(job.id);
      setStatus("pending");
    } catch (err) {
      const msg = getErrorMessage(
        err,
        "Não foi possível realizar sua candidatura."
      );

      showToast(msg, "error");
    } finally {
      setApplying(false);
      setShowQuestions(false);
    }
  };

  const userType = localStorage.getItem("userType");

  const handleApply = () => {
    if (userType !== "Freelancer") {
      showToast(
        "Entre com uma conta de freelancer para se candidatar.",
        "error"
      );
      return;
    }

    if (hasQuestions) {
      setShowQuestions(true);
      return;
    }

    doApply();
  };
  
  const handleCancel = async () => {
    if (!job?.id) return;
    if (!confirm("Cancelar candidatura?")) return;

    setApplying(true);
    try {
      await cancelApplication(job.id);
      setStatus(null);
    } catch (err) {
      const msg = getErrorMessage(
        err,
        "Não foi possível cancelar sua candidatura."
      );

      showToast(msg, "error");
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="pv-wrapper">
        <div className="pv-loading">Carregando vaga...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pv-wrapper">
        <EmptyState message={error} />
      </div>
    );
  }

  if (!job) return null;

  const renderButton = () => {
    if (status === "approved") {
      return <span className="pv-status pv-status--approved">Aprovado</span>;
    }

    if (status === "rejected") {
      return <span className="pv-status pv-status--rejected">Recusado</span>;
    }

    if (status === "pending") {
      return (
        <button className="pv-apply-btn" onClick={handleCancel} disabled={applying}>
          {applying ? "Cancelando..." : "Cancelar candidatura"}
        </button>
      );
    }

    return (
      <button className="pv-apply-btn" onClick={handleApply} disabled={applying}>
        {applying ? "Enviando..." : "Candidatar-se"}
      </button>
    );
  };

  return (
    <div className="pv-wrapper">
      <div className="pv-card">

        <div className="pv-header">
          <span className="pv-eyebrow">Passe de trabalho</span>
          <h1 className="pv-title">{job.title}</h1>
          <span className="pv-payment">
            R$ {Number(job.payment).toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
            })}
          </span>
        </div>

        <p className="pv-description">{job.description}</p>

        <div className="pv-info">
          <div className="pv-info-row">
            <MapPin size={16} className="pv-info-icon" />
            <span className="pv-info-label">Local</span>
            <span className="pv-info-value">{job.location}</span>
          </div>

          <div className="pv-info-row">
            <Calendar size={16} className="pv-info-icon" />
            <span className="pv-info-label">Data</span>
            <span className="pv-info-value">{job.date}</span>
          </div>

          <div className="pv-info-row">
            <Clock size={16} className="pv-info-icon" />
            <span className="pv-info-label">Horário</span>
            <span className="pv-info-value">{job.startTime} - {job.endTime}</span>
          </div>

          {job.dressCode && (
            <div className="pv-info-row">
              <Shirt size={16} className="pv-info-icon" />
              <span className="pv-info-label">Vestimenta</span>
              <span className="pv-info-value">{job.dressCode}</span>
            </div>
          )}

          {job.breakTime && (
            <div className="pv-info-row">
              <Coffee size={16} className="pv-info-icon" />
              <span className="pv-info-label">Intervalo</span>
              <span className="pv-info-value">{job.breakTime} min</span>
            </div>
          )}
        </div>

        <div className="pv-actions">
          {renderButton()}
        </div>

      </div>

      {showQuestions && hasQuestions && (
        <QuestionsModal
          questions={questions}
          onConfirm={doApply}
          onClose={() => setShowQuestions(false)}
          showToast={showToast}
        />
      )}

      <Toast
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ message: "", type: "success" })}
      />
    </div>
  );
}